import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { company } from "@/data/site";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms & Conditions | McKay Roofing & Solar" },
      { name: "description", content: "Terms governing the use of the McKay Roofing & Solar website and our SMS and email communications." },
      { property: "og:title", content: "Terms & Conditions – McKay Roofing & Solar" },
      { property: "og:description", content: "The terms that apply when you use our website or opt into our SMS and email programs." },
      { property: "og:url", content: "https://mckay.steadyautogrowthdemo.com/terms" },
    ],
    links: [{ rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  const updated = "January 14, 2026";
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        {/* Hero – background only */}
        <section className="relative w-full overflow-hidden bg-primary text-primary-foreground">
          <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/85" />
          <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_20%_20%,hsl(var(--secondary)/0.35),transparent_45%),radial-gradient(circle_at_80%_60%,hsl(var(--primary-foreground)/0.15),transparent_50%)]" />
          <div className="relative mx-auto flex min-h-[50vh] w-full max-w-5xl items-center px-4 py-20 sm:px-6 lg:px-8">
            <div>
              <h1 className="text-5xl font-bold sm:text-6xl text-balance">Terms &amp; Conditions</h1>
              <p className="mt-4 text-primary-foreground/80">Last updated: {updated}</p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-3xl px-4 py-14 sm:px-6 lg:px-8 prose prose-slate max-w-none">
          <p>
            These Terms &amp; Conditions govern your use of {company.name} services, including our
            website and our SMS and email communications programs. By using our services or opting into
            our communications, you agree to these Terms and our Privacy Policy.
          </p>

          <h2 className="mt-8 text-2xl font-bold">1. Program description</h2>
          <p>
            {company.name} may send SMS and email messages to users who opt in. Messages may include
            promotional offers, service updates, appointment confirmations and appointment reminders.
            Message frequency may vary.
          </p>

          <h2 className="mt-8 text-2xl font-bold">2. Consent to receive messages</h2>
          <p>
            By providing your phone number and opting in, you authorize {company.name} to send text
            messages to your mobile number. Consent is not a condition of purchase.
          </p>

          <h2 className="mt-8 text-2xl font-bold">3. Opt out</h2>
          <p>
            You can cancel the SMS service at any time. Just text "STOP" to {company.phone}. After you
            send "STOP", we will send an SMS confirming that you have been unsubscribed. After that,
            you will no longer receive SMS messages from us.
          </p>

          <h2 className="mt-8 text-2xl font-bold">4. Opt in again</h2>
          <p>
            If you want to rejoin after opting out, you can opt in again using the same method you
            originally used to enroll.
          </p>

          <h2 className="mt-8 text-2xl font-bold">5. Help and support</h2>
          <p>
            If you are experiencing issues with the messaging program, reply with the keyword HELP for
            assistance, or contact us directly at{" "}
            <a href={`mailto:${company.email}`} className="text-primary underline">{company.email}</a>{" "}
            or <a href={company.phoneHref} className="text-primary underline">{company.phone}</a>.
          </p>

          <h2 className="mt-8 text-2xl font-bold">6. Message frequency and rates</h2>
          <p>
            Message and data rates may apply for any messages sent to you from us and to us from you.
            You will receive up to 4 messages a month. If you have questions about your text or data
            plan, please contact your wireless provider.
          </p>

          <h2 className="mt-8 text-2xl font-bold">7. Carrier disclaimer</h2>
          <p>Carriers are not liable for delayed or undelivered messages.</p>

          <h2 className="mt-8 text-2xl font-bold">8. Privacy</h2>
          <p>
            Your use of the messaging program is also governed by our{" "}
            <a href="/privacy" className="text-primary underline">Privacy Policy</a>.
          </p>

          <h2 className="mt-8 text-2xl font-bold">9. Changes to terms</h2>
          <p>We may update these Terms from time to time. Updates will be posted with a new "Last updated" date.</p>

          <h2 className="mt-8 text-2xl font-bold">10. Contact</h2>
          <p>
            {company.name}<br />
            9520 Padgett St, Suite 107, San Diego, CA 92126<br />
            <a href={company.phoneHref} className="text-primary underline">{company.phone}</a><br />
            <a href={`mailto:${company.email}`} className="text-primary underline">{company.email}</a>
          </p>
        </section>
        <CTABlock title="Ready for a free roofing or solar quote?" subtitle="Tell us about your project. We usually respond same day." />
      </main>
      <SiteFooter />
    </div>
  );
}
